import { DialogWithForm } from '@strata/react-dialog';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { ClinicalPolicyStatusIndicator } from '@strata/react-status-indicators';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { put, useGet } from 'services/Http';

export const RequestReviewDialog = ({
    isOpen,
    isExternal,
    fetchPolicy,
    onSecondaryButtonClick,
    statusHistory = undefined,
    payerId,
    draftId,
}) => {
    const payerDetails = useGet(`/reference/payers/${payerId}`);
    const reviewerName = isExternal ? payerDetails?.payer_name : 'FMH team members';

    return (
        <Formik
            initialValues={{
                reviewer: statusHistory?.find((change) =>
                    isExternal
                        ? // For external review, set reviewer to last external change requestor if there is one
                          change.policy_status === ClinicalPolicyStatusIndicator.Statuses.EXTCHANGE
                        : // For internal review, set reviewer to last internal change requestor or internal approver if there is one
                          change.policy_status ===
                              ClinicalPolicyStatusIndicator.Statuses.INTCHANGE ||
                          change.policy_status ===
                              ClinicalPolicyStatusIndicator.Statuses.INTAPPROVE,
                )?.username,
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                reviewer: isExternal
                    ? Yup.string()
                          .required(validationStrings.requiredField.errorMessage)
                          .email('Must be a valid email')
                    : Yup.string()
                          .required(validationStrings.requiredField.errorMessage)
                          .matches(/@freemarkethealth.com$/, 'Must be a FMH email'),
                comment: Yup.string(),
            })}
            onSubmit={async ({ reviewer, comment }, { resetForm }) => {
                try {
                    await put(`/clinicalpolicy/admin/${payerId}/drafts/${draftId}/for_review`, {
                        policy_status: isExternal
                            ? ClinicalPolicyStatusIndicator.Statuses.EXTREVIEW
                            : ClinicalPolicyStatusIndicator.Statuses.INTREVIEW,
                        reviewer,
                        comment,
                    });
                    await fetchPolicy();
                    resetForm();
                    onSecondaryButtonClick();
                } catch (err) {
                    handleError(
                        `Error submitting ${isExternal ? 'external' : 'internal'} review request.`,
                        err,
                    );
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={`Request ${isExternal ? 'External' : 'Internal'} Review`}
                    primaryButtonLabel="Send Request"
                    secondaryButtonLabel="Cancel"
                    isOpen={isOpen}
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onSecondaryButtonClick();
                    }}
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <p className="margin__bottom--large">
                                You can request review from {reviewerName}. They will be notified
                                and asked to review this policy.
                            </p>
                            <TextInputFormik
                                id="reviewer"
                                label="Reviewer Email"
                                // TODO DEV-16294 uncomment helper text
                                // helperText={
                                //     !isExternal
                                //         ? 'You cannot select yourself as a reviewer.'
                                //         : undefined
                                // }
                                required
                            />
                            <TextAreaFormik id="comment" label="Comment" />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

RequestReviewDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isExternal: PropTypes.bool.isRequired,
    fetchPolicy: PropTypes.func.isRequired,
    onSecondaryButtonClick: PropTypes.func.isRequired,
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    payerId: PropTypes.string.isRequired,
    draftId: PropTypes.string.isRequired,
};
